import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CLIENT_DETAILS_QUERY_KEY, CLIENT_QUERY_KEY } from '@/app/(main)/client-detail/consts/client-detail';
import { CLIENT_GROUPED_QUERY_KEY } from '@/app/(main)/client-detail/hooks/useGetClientGroupedQuery';
import { RELATED_TRAVELERS_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { useEditClientMutation } from '@/app/(main)/clients/hooks/useEditClientMutation';
import type { CreateClientFormData, EditClientData } from '@/types/clientsApiTypes';
import { nullableString } from '@/utils/validations';
import { EditRelatedAddressForm } from '../../EditSectionModals/components/EditRelatedAddressForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { getRelatedAddressPayload } from '../../EditSectionModals/utils/getAddressPayload';

const schema = yup.object({
  primary_passenger: yup.object().shape({
    client: yup.object().shape({
      client_mailing_address: nullableString().optional(),
      client_mailing_address_line_2: nullableString().optional(),
      client_mailing_city: nullableString().optional(),
      client_mailing_state: nullableString().optional(),
      client_mailing_country: nullableString().optional(),
      zip_code: nullableString()
        .optional()
        .max(20, 'The client zip code field must not be greater than 20 characters.'),
    }),
  }),
  related_passengers: yup.array().of(
    yup.object().shape({
      client: yup.object().shape({
        same_as_primary: yup.boolean().optional().defined().default(false),
        client_mailing_address: nullableString().optional().defined(),
        client_mailing_address_line_2: nullableString().optional(),
        client_mailing_city: nullableString().optional().defined(),
        client_mailing_state: nullableString().optional().defined(),
        client_mailing_country: nullableString().optional().defined(),
        zip_code: nullableString()
          .optional()
          .defined()
          .max(20, 'The client zip code field must not be greater than 20 characters.'),
      }),
    })
  ),
});

export type RelatedAddressFormValues = yup.InferType<typeof schema>;

function useRelatedAddressOnSave() {
  const mutation = useEditClientMutation();
  const queryClient = useQueryClient();

  return async (payload: Partial<EditClientData>) => {
    await mutation.mutateAsync(
      {
        data: payload,
        clientId: Number(payload.client?.id),
      },
      {
        onSuccess: () => {
          toast.success('Client updated successfully');
          queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_GROUPED_QUERY_KEY] });
        },
      }
    );
  };
}

export const relatedAddressSection = defineSection<
  (typeof ModalKeys)['related_address'],
  CreateClientFormData,
  typeof schema,
  Partial<EditClientData>
>({
  key: ModalKeys.related_address,
  title: 'Address',
  subtitle: 'Passenger mailing address on record.',
  schema,
  mapClientToValues: (c) => ({
    primary_passenger: { client: c.primary_passenger.client },
    related_passengers: c.related_passengers.map((passenger) => ({
      client: {
        ...passenger.client,
        client_mailing_address: passenger.client.client_mailing_address ?? '',
        client_mailing_address_line_2: passenger.client.client_mailing_address_line_2 ?? '',
        client_mailing_city: passenger.client.client_mailing_city ?? '',
        client_mailing_state: passenger.client.client_mailing_state ?? '',
        client_mailing_country: passenger.client.client_mailing_country ?? '',
        zip_code: passenger.client.zip_code ?? '',
      },
    })),
  }),
  mapValuesToPayload: (v, client): Partial<EditClientData> => ({
    client: getRelatedAddressPayload({
      ...v.primary_passenger.client,
      client_id: client?.related_passengers?.[0]?.client.client_id ?? 0,
      client_mailing_address: v?.related_passengers?.[0]?.client?.client_mailing_address ?? '',
      client_mailing_address_line_2: v?.related_passengers?.[0]?.client?.client_mailing_address_line_2 ?? '',
      client_mailing_city: v?.related_passengers?.[0]?.client?.client_mailing_city ?? '',
      client_mailing_state: v?.related_passengers?.[0]?.client?.client_mailing_state ?? '',
      client_mailing_country: v?.related_passengers?.[0]?.client?.client_mailing_country ?? '',
      zip_code: v?.related_passengers?.[0]?.client?.zip_code ?? '',
    }),
  }),
  useOnSave: useRelatedAddressOnSave,
  Fields: () => <EditRelatedAddressForm />,
});